<template>
  <div>
    <div class="ibox ibox-job-invoice" v-if="jobIsCompleted">
      <div class="ibox-content ibox-content-padding-xs-slim">
        <div class="row">
          <div class="col-xs-6 col-sm-4 col-lg-4">
            <h2>Invoice</h2>
          </div>
          <div class="col-xs-6 col-sm-4 col-lg-5">
            <p
              class="invoice-status"
              :class="{
                'text-success': jobDecorated.invoice.isPaid,
                'text-danger': jobDecorated.invoice.isOverdue,
                'text-info': jobDecorated.invoice.isCurrent
              }">
              {{ jobDecorated.invoice.status_text }}
            </p>
          </div>
          <div class="col-xs-12 col-sm-4 col-lg-3">
            <router-link :to="{ name: 'invoices.show', params: { id: job.invoice.id } }" class="btn btn-sm btn-default">
              View <i class="fa fa-angle-right"></i>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <job-setup v-if="canSeeJobSetup && !jobIsPending"></job-setup>

    <div class="row">
      <div class="col-lg-6">

        <div class="ibox">
          <div class="ibox-title">
            <h2>
              Job Overview
                <button v-if="job.status != 'pending' && job.status != 'declined' && job.status != 'cancelled'" class="btn btn-info btn-circle pull-right" style="margin-left: 1rem;" @click="copyJob">
                  <i class="fa fa-copy"></i>
                </button>

                <router-link
                v-if="canEditJob"
                :to="{ name: 'jobs.edit', params: { id: job.id } }"
                class="btn btn-info btn-circle pull-right">
                  <i class="fa fa-pencil"></i>
                </router-link>
            </h2>
          </div>
          <div class="ibox-content ibox-content-padding-xs">

            <div class="job-overview-details">
              <div class="row row-clear-sm-6">
                <template v-if="canSeeClientInformation">
                  <div class="col-sm-6">
                    <p>
                      <strong>Client</strong><br />
                      {{ job.user.profile.full_name }}
                    </p>
                  </div>

                  <div class="col-sm-6">
                    <p>
                      <strong>Company</strong><br />
                      {{ job.user.profile.company_name }}
                    </p>
                  </div>
                </template>

                <div class="col-sm-6">
                  <p>
                    <strong>Start Date</strong><br />
                    {{ jobDecorated.start_at }}
                  </p>
                </div>

                <div class="col-sm-6">
                  <p>
                    <strong>End Date</strong><br />
                    {{ jobDecorated.end_at }}
                  </p>
                </div>

                <div class="col-sm-6">
                  <p>
                    <strong>Quantity</strong><br />
                    {{ jobDecorated.quantity }}
                  </p>
                </div>

                <div class="col-sm-6">
                  <p>
                    <strong>Commodity</strong><br />
                    {{ jobDecorated.commodity ? jobDecorated.commodity.name : '&ndash;' }}
                  </p>
                </div>

                <div class="col-sm-6">
                  <p>
                    <strong>Order</strong><br />
                    {{ jobDecorated.order_reference }}
                  </p>
                </div>

                <div class="col-sm-6">
                  <p>
                    <strong>Rate</strong><br />
                    {{ jobDecorated.rate }}
                  </p>
                </div>

                <div class="col-sm-6">
                  <p class="material-details">
                    <strong>Pickup Reference</strong><br />
                    {{ jobDecorated.pickup_reference }}
                  </p>
                </div>

                <div class="col-sm-6" v-if="distance">
                  <p>
                    <strong>Approximate Distance</strong><br />
                    {{ distance }}
                  </p>
                </div>

                <div class="col-sm-6">
                  <p class="material-details">
                    <strong>Delivery Reference</strong><br />
                    {{ jobDecorated.delivery_reference }}
                  </p>
                </div>

                <div class="col-sm-6" v-if="calculation">
                  <p>
                    <strong>Calculation</strong><br />
                    {{ calculation }} cents
                  </p>
                </div>

              </div>
            </div>

            <p class="material-details">
              <strong>Job Notes</strong><br />
              {{ jobDecorated.notes }}
            </p>

            <notifications v-if="$user.isClient"></notifications>

          </div>
        </div>

        <div class="ibox">
          <div class="ibox-title">
            <h2>Progress</h2>
          </div>
          <div class="ibox-content ibox-content-padding-xs">
            <div class="job-overview-details">
              <div class="progress">
                <div
                  :style="{ width: `${progressAssignedPercentage}%` }"
                  aria-valuemax="100"
                  aria-valuemin="0"
                  :aria-valuenow="progressAssignedPercentage"
                  role="progressbar"
                  class="progress-bar-assigned">
                  <span class="sr-only">{{ progressAssignedPercentage }}% Assigned</span>
                </div>

                <div
                  v-if="progressCompletedPercentage > 0"
                  :style="{ width: `${progressCompletedPercentage}%` }"
                  aria-valuemax="100"
                  aria-valuemin="0"
                  :aria-valuenow="progressCompletedPercentage"
                  role="progressbar"
                  class="progress-bar progress-bar-primary">
                  <span class="sr-only">{{ progressCompletedPercentage }}% Complete</span>
                </div>
              </div>

              <div class="row row-progress-text">
                <div class="col-xs-4">
                  {{ tonnageCompleted }}
                </div>
                <div class="col-xs-4 text-center">
                  of
                </div>
                <div class="col-xs-4 text-right">
                  {{ jobDecorated.quantity }}
                </div>
              </div>

              <p class="running-total">
                <strong>running total</strong> {{ jobRunningTotal }}
              </p>
            </div>

          </div>
        </div>

      </div>
      <div class="col-lg-6">

        <div class="ibox">
          <div class="ibox-title">
            <h2>Locations</h2>
          </div>
          <div class="ibox-content ibox-content-padding-xs">

            <div class="job-overview-details">
              <div class="row">
                <div class="col-sm-6">
                  <location-detail :location="jobDecorated.location_from" title="From"></location-detail>
                </div>
                <div class="col-sm-6">
                  <location-detail :location="jobDecorated.location_to" title="To"></location-detail>
                </div>
              </div>

              <div class="map-container">
                <distance-map ref="map" :location-from="jobDecorated.location_from" :location-to="jobDecorated.location_to"></distance-map>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <component v-if="haveJob && !jobIsPending" :is="loadsComponent"></component>

    <history v-if="canSeeJobHistory"></history>

    <ladda-button v-if="canCancelJob" :on-click="handleCancelJob" class="btn btn-sm btn-danger pull-right">
      <i class="fa fa-times"></i> Cancel
    </ladda-button>

    <div class="margin-20"></div>

    <location-detail-modal :edit="handleEditLocation"></location-detail-modal>
    <location-drawer
      v-if="editingExistingLocation"
      :existing-location="locationBeingEdited"
      :done="locationDoneCallback"
      :closed="locationDrawerClosed">
    </location-drawer>
  </div>
</template>

<script>
  import { mapActions, mapGetters, mapMutations } from 'vuex'
  import { SET_JOB_ATTRIBUTE, RESET_JOB } from 'vx/mutations'
  import { clone } from 'lodash'
  import { jobProgressHelpers, jobHelpers } from 'mixins/jobs'
  import { LaddaButton } from 'components/forms'
  import { alert, notify, http } from 'utils'
  import ClientLoadList from './loads/ClientLoadList'
  import AdminLoadList from './loads/AdminLoadList'
  import DistanceMap from './DistanceMap'
  import History from './History'
  import JobSetup from './JobSetup'
  import Notifications from './Notifications'
  import LocationDetail from './LocationDetail'
  import LocationDetailModal from './LocationDetailModal'
  import LocationDrawer from './LocationDrawer'
  import $ from 'jquery'

  export default {
    mixins: [jobProgressHelpers, jobHelpers],

    data () {
      return {
        editingExistingLocation: false,
        locationBeingEdited: {},
        distance: null,
        calculation: null
      }
    },

    components: {
      DistanceMap,
      ClientLoadList,
      AdminLoadList,
      JobSetup,
      Notifications,
      History,
      LocationDetail,
      LocationDetailModal,
      LocationDrawer,
      LaddaButton
    },

    created () {
      this[RESET_JOB]()

      this.getJob(this.$route.params.id).then(response => {
        // Admins/Coordinators should not be able to see this page if the job is pending
        if (this.$user.isAdminOrCoordinator && response.data.status === 'pending') {
          notify.error('Job currently pending.')
          this.$router.push({ name: 'dashboard' })
        }

        this.getJobDistance()
      })
    },

    computed: {
      ...mapGetters('job', ['jobDecorated']),
      ...mapGetters('user', ['userOptionsWithNone']),

      /**
       * Determine if we can cancel the job.
       *
       * @return boolean
       */
      canCancelJob () {
        if (this.jobIsNotEditable) {
          return false
        }

        return this.$user.isAdminOrCoordinator || this.jobIsPending
      },

      /**
       * Determine if we can edit the job.
       *
       * @return boolean
       */
      canEditJob () {
        if (this.jobIsNotEditable || this.haveNoJob) {
          return false
        }

        return this.$user.isAdminOrCoordinator || this.jobIsPending
      },

      /**
       * Determine if we can see the client information.
       *
       * @return boolean
       */
      canSeeClientInformation () {
        return this.$user.isAdminOrCoordinator && this.haveJob
      },

      /**
       * Determine if the user can see the job setup.
       *
       * @return boolean
       */
      canSeeJobSetup () {
        return this.$user.isAdminOrCoordinator && this.haveJob
      },

      /**
       * Determine if the user can see the job history.
       *
       * @return boolean
       */
      canSeeJobHistory () {
        return this.$user.isAdminOrCoordinator && this.haveJob
      },

      /**
       * The load details component we need to use based on the user role.
       *
       * @return string
       */
      loadsComponent () {
        if (this.$user.isAdminOrCoordinator) {
          return 'admin-load-list'
        }

        return 'client-load-list'
      }
    },

    methods: {
      ...mapActions('job', ['getJob', 'updateJob', 'cancelJob']),
      ...mapActions('user', ['getUsers']),
      ...mapMutations('job', [SET_JOB_ATTRIBUTE, RESET_JOB]),

      async copyJob () {
        const response = await http.post(`jobs/${this.job.id}/copy`)

        if (response.status === 200) {
          notify.success('Job copied successfully.')
          this.$router.push(`${response.data.id}/edit`)
        } else {
          notify.error('There was an error copying the job.', response.error, response.data)
        }
      },

      /**
       * Handler for saving a location.
       *
       * @param object location
       * @param integer id
       *
       * @return void
       */
      locationDoneCallback (location, id) {
        // With the ID of the original we check if we need to replace the location from
        // or the location to (or even both).
        if (this.job.location_from !== null && this.job.location_from.id === id) {
          this[SET_JOB_ATTRIBUTE]({ attribute: 'location_from', value: location })
          this[SET_JOB_ATTRIBUTE]({ attribute: 'location_from_id', value: location.id })
        }

        if (this.job.location_to !== null && this.job.location_to.id === id) {
          this[SET_JOB_ATTRIBUTE]({ attribute: 'location_to', value: location })
          this[SET_JOB_ATTRIBUTE]({ attribute: 'location_to_id', value: location.id })
        }

        this.$nextTick(() => {
          this.$refs.map.$emit('refresh')
        })

        this.updateJob({
          id: this.job.id,
          payload: {
            user_id: this.job.user_id,
            location_from_id: this.job.location_from_id,
            location_to_id: this.job.location_to_id
          }
        })
      },

      async getJobDistance () {
        const response = await http.get(`job/${this.job.id}/distance`)
        this.distance = response.data.distance
        if (this.distance === null) {
          return 0
        }

        this.calculation = parseFloat((this.job.rate * 100) / parseFloat(this.distance.replace(/[^0-9.]/g, ''))).toFixed(2)
      },

      /**
       * Handler for location drawer being closed.
       *
       * @return void
       */
      locationDrawerClosed () {
        this.locationBeingEdited = {}
        this.editingExistingLocation = false
      },

      /**
       * Handle editing a location.
       *
       * @param object location
       *
       * @return void
       */
      handleEditLocation (location) {
        $('.modal-location-details').modal('hide')

        this.locationBeingEdited = clone(location)
        this.editingExistingLocation = true
      },

      /**
       * Handle cancelling the job.
       *
       * @return Promise
       */
      handleCancelJob () {
        return alert.delete({
          title: 'Caution',
          text: 'This will cancel the job and all associated loads. Are you sure you want to continue?'
        }).then(() => {
          return this.cancelJob(this.job.id)
        })
      }
    }
  }
</script>



// WEBPACK FOOTER //
// Show.vue?04e5594d